import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import ReactGA4 from 'react-ga4';
import { IconReferral } from '@/assets';
import { CommonModal } from '../Modal/CommonModal';
import {
  useCurrentAddressInfo,
  useDebounce,
  useIsProfilePage,
  useSwitch,
  useUserType,
} from '@/hooks';
import Input from '../Input';
import Button from '../Button';
import { useLocalReferralCode, useUserReferralCode, useValidateReferralCode } from './hooks';
import { CopyButton } from '../CopyButton';
import { integrateQueryToUrl, login, wrapModalPromise } from '@/utils';

import style from './index.module.scss';

export const ReferralCodeBtn = ({ className }: { className?: string }) => {
  const { on, turnOn, turnOff } = useSwitch();
  const isInProfile = useIsProfilePage();
  const userType = useUserType();

  const handleClick = useCallback(() => {
    turnOn();
    if (isInProfile) {
      ReactGA4.event({
        category: 'Click Profile Icon',
        action: `Icon_Share__${userType}`,
      });
    }
  }, [isInProfile, turnOn, userType]);

  return (
    <>
      <IconReferral className={clsx(style.referralCodeBtn, className)} onClick={handleClick} />
      {on ? <ReferralModal hide={turnOff} /> : null}
    </>
  );
};

const ReferralModal = ({
  hide,
  link,
  isQuest,
}: {
  hide?: () => void;
  link?: string;
  isQuest?: boolean;
}) => {
  const { t } = useTranslation();

  const pageUrl = link || window.location.href;
  const { isVerified, address } = useCurrentAddressInfo();
  const { localReferralCode, setLocalReferralCode } = useLocalReferralCode(address);
  const [debounceReferralCode] = useDebounce(localReferralCode);
  const { data: isValid, mutate: validateCode, isError, isLoading } = useValidateReferralCode();
  const isDirty = useRef(false);

  useEffect(() => {
    if (!debounceReferralCode) return;
    validateCode(Number(debounceReferralCode));
  }, [debounceReferralCode, validateCode]);

  const { data: userRefCode, refetch, isLoading: isLoadingUserRefCode } = useUserReferralCode();

  useEffect(() => {
    if (!isDirty.current && !localReferralCode && userRefCode?.ref_code) {
      setLocalReferralCode(userRefCode.ref_code?.toString());
    }
  }, [userRefCode?.ref_code, setLocalReferralCode, localReferralCode]);

  const handleChange = useCallback(
    (v: string) => {
      isDirty.current = true;
      setLocalReferralCode(v);
    },
    [setLocalReferralCode],
  );

  const shareUrl = useMemo(() => {
    if (!localReferralCode) return pageUrl;
    return integrateQueryToUrl(pageUrl, { t: `${new Date().getTime()}`, r: localReferralCode });
  }, [localReferralCode, pageUrl]);

  const isInvalidCode = useMemo(
    () => (isValid === false || isError) && !!localReferralCode,
    [isValid, isError, localReferralCode],
  );

  const isButtonDisabled = useMemo(
    () => isInvalidCode || !localReferralCode || isLoading,
    [isInvalidCode, isLoading, localReferralCode],
  );

  const handleClickGenerate = useCallback(async () => {
    if (!isVerified) {
      login();
      return;
    }
    await refetch();
  }, [isVerified, refetch]);

  return (
    <CommonModal containerClassName={style.modal} turnOff={hide}>
      <div className={style.container}>
        <div className={style.title}>{t('ReferralCodeBtn.tfkk9m')}</div>
        <div className={style.tipText}>
          {isQuest ? t('ReferralCodeBtn.9yec06') : t('ReferralCodeBtn.102k3t3')}
        </div>
        <div className={style.subTitle}>{t('ReferralCodeBtn.y7plqm')}</div>
        <div className={style.sharePage}>{pageUrl}</div>
        <div className={style.subTitle}>{t('ReferralCodeBtn.dp70q8')}</div>
        <div className={style.codeInputContainer}>
          <Input
            className={style.codeInput}
            autoFocus
            disabled={isLoadingUserRefCode}
            value={localReferralCode}
            placeholder={t('ReferralCodeBtn.1qpkbqt')}
            onChange={handleChange}
          />
          {isInvalidCode && <span className={style.invalidTip}>{t('ReferralCodeBtn.agsbrq')}</span>}
        </div>
        <div className={clsx(style.tipText, style.myCode)}>
          {t('ReferralCodeBtn.mz9666')}
          {userRefCode?.ref_code || 'not set'}
        </div>
        {!userRefCode?.ref_code && (
          <span className={style.generateBtn} onClick={() => handleClickGenerate()}>
            {t('ReferralCodeBtn.1vlvltx')}
          </span>
        )}
        <div className={style.tipText}>{t('ReferralCodeBtn.1j5edl')}</div>
        <div className={style.subTitle}>{t('ReferralCodeBtn.shvtqj')}</div>
        <div className={style.shareContainer}>
          <div className={style.sharedURL}>{shareUrl}</div>
          <CopyButton disabled={isButtonDisabled} text={shareUrl}>
            <Button
              loading={isLoading}
              disabled={isButtonDisabled}
              className={style.shareBtn}
              type="primary">
              {t('ReferralCodeBtn.1rpfjno')}
            </Button>
          </CopyButton>
        </div>
      </div>
    </CommonModal>
  );
};

export const openReferralModal = wrapModalPromise(ReferralModal, {
  wrapInCommonModal: false,
});
